<script setup>
// Importing the router. Move this line to new home page in the future
import { RouterView, useRoute } from "vue-router";
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router"; // Importing the router
import { supabase } from "@/lib/supabase";

const displayNavBar = ref(true); // Displaying the nav bar
const activeHighlightClass = ref(null); // Highlighting the active class
const router = useRouter(); // Getting the router
const route = useRoute(); // Getting the current route

// Mobile sidebar toggle state
const isMobile = ref(false);
const isSidebarOpen = ref(false);

// Set up responsive detection and body scroll lock
let mql;

// Ensure the body background reflects the current theme as early as possible,
// so computed styles (used for status bar color) read the correct value immediately.
(() => {
  try {
    const prefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const theme =
      document.documentElement.getAttribute("data-theme") ||
      (prefersDark ? "dark" : "light");
    document.body.style.backgroundColor =
      theme === "dark" ? "#0f172a" : "#ffffff";
  } catch {
    // Swallow errors: best-effort early theme init
    void 0;
  }
})();
const setThemeColor = (color) => {
  let meta =
    document.querySelector('meta#dynamic-theme-color[name="theme-color"]') ||
    document.querySelector('meta[name="theme-color"]');
  if (!meta) return; // Rely on index.html-provided tag; do not create
  meta.setAttribute("content", color);
};

const updateThemeColorFromBg = () => {
  try {
    const bg =
      getComputedStyle(document.body).backgroundColor ||
      getComputedStyle(document.documentElement).backgroundColor ||
      "#ffffff";
    setThemeColor(bg.trim());
  } catch {
    setThemeColor("#ffffff");
  }
};

const applyTheme = (isDark) => {
  const root = document.documentElement;
  const theme = isDark ? "dark" : "light";
  root.setAttribute("data-theme", theme);
  // Immediately set body background to match theme so computed styles are correct early
  document.body.style.backgroundColor =
    theme === "dark" ? "#0f172a" : "#ffffff";
  updateThemeColorFromBg();
};
const loadDarkModePref = async () => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      applyTheme(false);
      return;
    }
    const { data } = await supabase
      .from("user_preferences")
      .select("dark_mode")
      .eq("user_id", user.id)
      .maybeSingle();
    applyTheme(!!data?.dark_mode);
  } catch (e) {
    console.warn("Failed to load dark_mode preference", e);
    applyTheme(false);
  }
};
const updateIsMobile = (matches) => {
  isMobile.value = matches;
  if (!matches) {
    // Ensure sidebar is closed and body scroll restored when switching to desktop
    isSidebarOpen.value = false;
    document.body.style.overflow = "";
  }
};

onMounted(() => {
  // Treat <= 768px as "mobile"
  mql = window.matchMedia("(max-width: 768px)");
  updateIsMobile(mql.matches);
  const handler = (e) => updateIsMobile(e.matches);
  // Add listener (support older browsers)
  mql.addEventListener
    ? mql.addEventListener("change", handler)
    : mql.addListener(handler);
  // Save handler for cleanup
  mql._handler = handler;

  // Load and apply dark mode preference
  loadDarkModePref();

  // Observe theme changes to keep meta theme-color in sync
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "attributes" && m.attributeName === "data-theme") {
        updateThemeColorFromBg();
      }
    }
  });
  obs.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  // Store observer for cleanup
  mql._themeObserver = obs;
});

// Cleanup listener on unmount
onBeforeUnmount(() => {
  if (mql) {
    mql.removeEventListener
      ? mql.removeEventListener("change", mql._handler)
      : mql.removeListener(mql._handler);
    if (mql._themeObserver) {
      mql._themeObserver.disconnect();
      delete mql._themeObserver;
    }
  }
});

// Lock body scroll only when sidebar is open on mobile
watch([isSidebarOpen, isMobile], ([open, mobile]) => {
  if (mobile && open) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
});

const routeSettings = {
  "/": {
    showNavBar: false,
  },
  "/home": {
    showNavBar: true,
    highlightClass: "home",
  },
  "/learn": {
    showNavBar: true,
    highlightClass: "cards",
  },
  "/account": {
    showNavBar: true,
    highlightClass: "account",
  },
  "/login": {
    showNavBar: false,
  },
  "/onboarding": {
    showNavBar: false,
  },
  "/verify-email": {
    showNavBar: false,
  },
  "/subscriptions": {
    showNavBar: false,
  },
  "/white-paper": {
    showNavBar: false,
  },
};

// Function to watch the route and update the nav bar and highlight class
watch(
  () => route.path,
  (newPath) => {
    const settings = routeSettings[newPath] ?? { showNavBar: true };

    // Default from route settings
    let showNav = settings.showNavBar;
    let highlight = settings.highlightClass ?? null;

    if (newPath.startsWith("/stats/")) {
      highlight = "home";
    }

    // Force-hide nav on all non-learn hosts (marketing domains)
    if (typeof window !== "undefined") {
      const h = window.location.hostname;
      const isLearn =
        h === "localhost" ||
        h === "127.0.0.1" ||
        h === "::1" ||
        h.startsWith("learn.");
      if (!isLearn) {
        showNav = false;
      }
    }

    // Special-case subscriptions route:
    // Show nav bar on learn.* (and localhost) but hide on marketing host.
    if (newPath === "/subscriptions" && typeof window !== "undefined") {
      const h = window.location.hostname;
      const isLearn =
        h === "localhost" ||
        h === "127.0.0.1" ||
        h === "::1" ||
        h.startsWith("learn.");
      showNav = isLearn;
      if (isLearn && !highlight) {
        // Optional: highlight an appropriate section when inside app
        highlight = "account";
      }
    }

    displayNavBar.value = showNav;
    activeHighlightClass.value = highlight;

    // Close the sidebar on route change when on mobile
    if (isMobile.value) {
      isSidebarOpen.value = false;
    }
    // Recompute theme color for iOS status bar in case view background changes
    updateThemeColorFromBg();
  },
  { immediate: true }
);

// Function to update the route
const goTo = (path) => {
  router.push(path);
  // Close the sidebar after navigation on mobile
  if (isMobile.value) {
    isSidebarOpen.value = false;
  }
};
</script>

<template>
  <!-- Adding a small bar navigation bar on the left hand side of the screen-->
  <!-- Permanent thin mobile header with animated arrow toggle -->
  <div class="safe-area-top" aria-hidden="true"></div>
  <div
    v-if="displayNavBar && isMobile && !isSidebarOpen"
    class="mobile-header"
    @click="isSidebarOpen = !isSidebarOpen"
    aria-label="Toggle navigation"
  >
    <span class="menu-label">Menu</span>
    <span class="arrow" :class="{ open: isSidebarOpen }"></span>
  </div>

  <!-- Dim overlay behind the sidebar on mobile; clicking it closes the sidebar -->
  <div
    v-if="displayNavBar && isMobile && isSidebarOpen"
    class="nav-overlay"
    @click="isSidebarOpen = false"
  ></div>

  <div
    v-if="displayNavBar"
    class="nav-bar"
    :class="{ 'is-mobile': isMobile, 'is-open': isMobile && isSidebarOpen }"
  >
    <button
      v-if="isMobile && isSidebarOpen"
      class="nav-close"
      @click.stop="isSidebarOpen = false"
      aria-label="Hide navigation"
    >
      <span class="arrow"></span>
    </button>
    <!-- Adding The Logo to the Nav Bar -->
    <router-link to="/home">
      <img
        @click="goTo('/home')"
        class="logo logo-symbol"
        src="./assets/Logo Symbol.svg"
        alt="Lexical logo symbol"
      />
      <img
        @click="goTo('/home')"
        class="logo logo-expanded"
        src="./assets/Logo Expanded.svg"
        alt="Lexical logo"
      />
    </router-link>
    <!-- Adding the Home icon to the Nav Bar as a button that routes '/' -->
    <router-link to="/home" @click="goTo('/home')">
      <div class="nav-item">
        <span class="icon-wrap">
          <img
            :class="['home', { active: activeHighlightClass === 'home' }]"
            id="icon"
            src="./assets/HomeIcon.svg"
            alt="home-icon"
          />
        </span>
        <span class="label">Home</span>
      </div>
    </router-link>
    <!-- Adding the Learn icon to the Nav Bar as a button that routes '/learn'-->
    <router-link to="/learn" @click="goTo('/learn')">
      <div class="nav-item">
        <span class="icon-wrap">
          <img
            :class="['cards', { active: activeHighlightClass === 'cards' }]"
            id="icon"
            src="./assets/CardsIcon.svg"
            alt="learn-icon"
          />
        </span>
        <span class="label">Learn</span>
      </div>
    </router-link>
    <!-- Adding the Account Icon to the Nav Bar -->
    <router-link to="/account" @click="goTo('/account')">
      <div class="nav-item">
        <span class="icon-wrap">
          <img
            :class="['account', { active: activeHighlightClass === 'account' }]"
            id="icon"
            src="./assets/AccountIcon.svg"
            alt="account-icon"
          />
        </span>
        <span class="label">Account</span>
      </div>
    </router-link>
  </div>
  <!-- Adding the router view to display the different pages-->
  <RouterView />
</template>

<style scoped>
/* Placeing the nav-bar on the left hand side of the screen*/
.nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 64px;
  height: 100%;
  background-color: var(--sidebar-bg);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 20px;
}

/* Styling the logo */
.logo {
  width: auto;
  height: 25px;
  margin-bottom: 10px;
  margin-left: 18px;
  align-self: flex-start; /* keep left-aligned in both collapsed and expanded states */
  display: block; /* remove baseline gap for inline images */
  vertical-align: top; /* avoid inline baseline alignment */
  object-fit: contain; /* consistent scaling for different SVG viewBoxes */
}

/* Swap logo based on nav state */
.logo-expanded {
  display: none;
}
.nav-bar:not(.is-mobile):hover .logo-symbol {
  display: none;
}
.nav-bar:not(.is-mobile):hover .logo-expanded {
  display: block;
}

/* On mobile when sidebar is open, treat as expanded */
.nav-bar.is-mobile .logo-symbol {
  display: none;
}
.nav-bar.is-mobile .logo-expanded {
  display: block;
}

/* Styling the icons */
#icon {
  width: 30px;
  height: 30px;
  margin-top: 5px;
  margin-bottom: 5px;
  opacity: 0.5;
}

#icon.active {
  opacity: 1;
}

/* Icon highlight wrapper surrounds the image only */
.icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  transition: 0.3s;
  padding: 5px; /* equal padding keeps the highlight square around the 30x30 icon */
}
.icon-wrap:hover {
  background-color: var(--accent);
}
.icon-wrap:has(#icon.active) {
  background-color: var(--accent);
}

/* Hover-to-expand and labels (desktop) */
.nav-bar:not(.is-mobile):hover {
  width: 190px;
  align-items: flex-start;
}

.nav-bar a {
  width: 100%;
  text-decoration: none;
}

.nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 10px;
  border-radius: 10px;
}
/* Center icons in collapsed desktop nav */
.nav-bar:not(.is-mobile) .nav-item {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
/* Left-align items when expanded on desktop */
.nav-bar:not(.is-mobile):hover .nav-item {
  justify-content: flex-start;
  padding-left: 10px;
  padding-right: 10px;
}

.nav-item:hover {
  background-color: transparent;
}

/* hover highlight kept at icon level via .icon-wrap:hover; removing nav-item highlight */
/* .nav-item:hover { background-color: var(--accent); } */

.nav-bar .label {
  opacity: 0;
  max-width: 0;
  white-space: nowrap;
  overflow: hidden;
  margin-left: 0;
  color: var(--text-color);
  transition: opacity 0.2s ease, max-width 0.2s ease, margin-left 0.2s ease;
  font-size: 20px;
}

/* Show labels when expanded on desktop */
.nav-bar:not(.is-mobile):hover .label {
  opacity: 1;
  max-width: 160px;
  margin-left: 8px;
}

/* Keep labels visible on mobile */
.nav-bar.is-mobile .label {
  opacity: 1;
  max-width: 200px;
  margin-left: 8px;
}

/* Mobile sidebar toggle and overlay styles */
/* Smooth slide-in/out for the sidebar */
.nav-bar {
  transition: transform 0.25s ease, width 0.2s ease;
}

/* On mobile, keep the sidebar off-canvas by default */
.nav-bar.is-mobile {
  width: 200px; /* reduced to ~2/3 for mobile */
  transform: translateX(-100%);
  background-color: #ffffff; /* force white background on mobile */
}
/* Dark mode mobile nav background */
[data-theme="dark"] .nav-bar.is-mobile {
  background-color: var(--sidebar-bg);
}

/* When open on mobile, slide it into view */
.nav-bar.is-mobile.is-open {
  transform: translateX(0);
}

/* Overlay shown behind the sidebar on mobile */
.nav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 90; /* below the sidebar (100), above content */
}

/* Mobile header with animated arrow toggle */
.mobile-header {
  position: fixed;
  top: 10px;
  left: 6px;
  height: 24px;
  background: transparent;
  display: none; /* shown via media query */
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  z-index: 120; /* above overlay and sidebar */
  box-shadow: none;
  border-radius: 8px;
  gap: 6px;
}

.mobile-header .arrow {
  width: 8px;
  height: 8px;
  border-right: 1.5px solid var(--text-color);
  border-top: 1.5px solid var(--text-color);
  transform: rotate(45deg);
  transition: transform 0.2s ease;
  animation: arrow-pulse 1.6s ease-in-out infinite;
}
.mobile-header .menu-label {
  color: var(--text-color);
  font-size: 14px;
  line-height: 1;
}

.mobile-header .arrow.open {
  transform: rotate(-135deg);
}

/* Close arrow inside mobile nav (top-right) */
.nav-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  background: transparent;
  border: none;
  padding: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 130; /* above nav contents */
}
.nav-bar.is-mobile.is-open .nav-close {
  display: flex;
}
.nav-close .arrow {
  width: 8px;
  height: 8px;
  border-left: 1.5px solid var(--text-color);
  border-bottom: 1.5px solid var(--text-color);
  transform: rotate(45deg); /* points left */
  transition: transform 0.2s ease;
}

@keyframes arrow-pulse {
  0%,
  100% {
    transform: translateX(0) rotate(45deg);
  }
  50% {
    transform: translateX(2px) rotate(45deg);
  }
}

/* Show header and adjust mobile layout */
@media (max-width: 768px) {
  .mobile-header {
    display: flex;
  }
  .nav-bar.is-mobile {
    top: 0;
    height: 100%;
  }
  .nav-overlay {
    top: 0;
    height: 100%;
  }
}
</style>

<style>
/* Global system font stack and theming */
:root {
  --app-bg: #ffffff;
  --text-color: #111827;
  --sidebar-bg: #f3f3f3;
  --hover-bg: #e6e6e6;
  --accent: #99b5d3;
  --mobile-header-h: 70px;
  /* Morpho chip backgrounds (light) */
  --morpho-feminine-bg: #fef1f0;
  --morpho-masculine-bg: #f2f8f9;
  /* Stat badge backgrounds (light) */
  --badge-red-bg: #fde2e2;
  --badge-orange-bg: #ffe8cc;
  --badge-green-bg: #d4edda;
}

[data-theme="dark"] {
  --app-bg: #0f172a; /* slate-900 */
  --text-color: #e5e7eb; /* gray-200 */
  --sidebar-bg: #1f2937; /* gray-800 */
  --hover-bg: #334155; /* slate-700 */
  --accent: #446488; /* muted accent for dark */
  /* Morpho chip backgrounds (dark) */
  --morpho-feminine-bg: #3f2a2f; /* muted rose */
  --morpho-masculine-bg: #27353a; /* muted teal/blue slate */
  /* Stat badge backgrounds (dark) */
  --badge-red-bg: #4a2f2f;
  --badge-orange-bg: #4a3a23;
  --badge-green-bg: #274231;
}

html {
  overflow-x: hidden;
  background: var(--app-bg);
}
body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  background: var(--app-bg);
  color: var(--text-color);
  overflow-x: hidden;
}

/* Ensure LearnView morpho chips use theme-aware colors */
.morpho-box.morpho-feminine {
  background: var(--morpho-feminine-bg) !important;
  color: var(--text-color) !important;
}
.morpho-box.morpho-masculine {
  background: var(--morpho-masculine-bg) !important;
  color: var(--text-color) !important;
}

/* Stat badges (HomeView) themed for light/dark */
.stat-badge.badge-red {
  background: var(--badge-red-bg) !important;
  color: var(--text-color) !important;
}
.stat-badge.badge-orange {
  background: var(--badge-orange-bg) !important;
  color: var(--text-color) !important;
}
.stat-badge.badge-green {
  background: var(--badge-green-bg) !important;
  color: var(--text-color) !important;
}
/* Make navbar icons white in dark mode (images only) */
[data-theme="dark"] .nav-bar img.logo,
[data-theme="dark"] .nav-bar .icon-wrap img#icon,
[data-theme="dark"] .nav-bar img#icon {
  filter: invert(1) brightness(1.6);
}
/* Fill iOS notch/status bar area with background color */
.safe-area-top {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: env(safe-area-inset-top);
  height: constant(safe-area-inset-top);
  background: var(--app-bg);
  pointer-events: none;
  z-index: 1;
}
</style>
